import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { StarRating } from '@/components/ui/StarRating';
import NudgeModal from '@/components/feature/NudgeModal';
import { useNudgeModal } from '@/hooks/useNudgeModal';
import {
  fetchSiteBySlug,
  fetchSiteReviews,
  createSiteReview,
  incrementReviewHelpful,
} from '@/repositories/sites';
import { fetchUsageRecipesBySite } from '@/repositories/recipes';
import { fetchSiteRatingBySiteId } from '@/repositories/siteRatings';
import { SITE_REVIEW_RATING_ITEMS } from '@/constants/siteReviewRatings';
import type { Site, SiteReview } from '@/types/sites';
import type { UsageRecipe } from '@/types/recipes';
import type { SiteRating } from '@/types/siteRatings';

const THREE_MONTHS_MS = 1000 * 60 * 60 * 24 * 90;

export default function SiteDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [site, setSite] = useState<Site | null>(null);
  const [allReviews, setAllReviews] = useState<SiteReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [updatingHelpful, setUpdatingHelpful] = useState<string | null>(null);
  const [recipes, setRecipes] = useState<UsageRecipe[]>([]);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [siteRating, setSiteRating] = useState<SiteRating | null>(null);
  const navigate = useNavigate();
  const reviewFormRef = useRef<HTMLDivElement>(null);

  // ナッジモーダルの制御（5秒後に30%の確率で表示）
  const nudgeModal = useNudgeModal({
    delayMs: 5000,
    probability: 0.3,
  });

  // フォーム状態
  const [formData, setFormData] = useState({
    role: '居場所スタッフ',
    title: '',
    body: '',
    rating_place: 3,
    rating_home: 3,
    rating_self: 3,
    rating_print: 3,
    rating_design: 3,
  });

  useEffect(() => {
    const loadData = async () => {
      if (!slug) return;

      try {
        setLoading(true);
        const siteData = await fetchSiteBySlug(slug);
        
        if (!siteData) {
          setError('この教材サイトは見つかりませんでした。');
          return;
        }

        setSite(siteData);
        const reviews = await fetchSiteReviews(siteData.id);
        setAllReviews(reviews);
        
        const rating = await fetchSiteRatingBySiteId(siteData.id);
        setSiteRating(rating);
      } catch (err) {
        console.error('Failed to load site data:', err);
        setError('データの読み込みに失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [slug]);

  useEffect(() => {
    async function loadRecipes() {
      if (!site) return;
      try {
        setRecipesLoading(true);
        const data = await fetchUsageRecipesBySite(site.id);
        setRecipes(data);
      } catch (error) {
        console.error('Failed to load recipes:', error);
      } finally {
        setRecipesLoading(false);
      }
    }
    loadRecipes();
  }, [site]);

  // 表示対象レビューのフィルタリング
  const visibleReviews = allReviews.filter((review) => {
    const created = new Date(review.created_at).getTime();
    const now = Date.now();
    const isRecent = now - created <= THREE_MONTHS_MS;
    const isVeryHelpful = review.helpful_count >= 30;
    return isRecent || isVeryHelpful;
  });

  // 平均評価の計算
  const calculateAverages = () => {
    if (visibleReviews.length === 0) {
      return null;
    }

    const placeAvg =
      visibleReviews.reduce((sum, r) => sum + r.rating_place, 0) / visibleReviews.length;
    const homeAvg =
      visibleReviews.reduce((sum, r) => sum + r.rating_home, 0) / visibleReviews.length;
    const selfAvg =
      visibleReviews.reduce((sum, r) => sum + r.rating_self, 0) / visibleReviews.length;
    const printAvg =
      visibleReviews.reduce((sum, r) => sum + r.rating_print, 0) / visibleReviews.length;
    const designAvg =
      visibleReviews.reduce((sum, r) => sum + r.rating_design, 0) / visibleReviews.length;
    const overallAvg = (placeAvg + homeAvg + selfAvg + printAvg + designAvg) / 5;

    return { placeAvg, homeAvg, selfAvg, printAvg, designAvg, overallAvg };
  };

  const averages = calculateAverages();

  // 口コミ投稿フォームへスクロールする関数
  const scrollToReviewForm = () => {
    if (reviewFormRef.current) {
      reviewFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // タイトル入力欄にフォーカス
      setTimeout(() => {
        const titleInput = reviewFormRef.current?.querySelector('input[type="text"]') as HTMLInputElement;
        if (titleInput) {
          titleInput.focus();
        }
      }, 500);
    }
  };

  // 口コミ投稿
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!site) return;

    if (!formData.title.trim() || !formData.body.trim()) {
      alert('タイトルと本文を入力してください。');
      return;
    }

    try {
      setSubmitting(true);
      await createSiteReview(site.id, formData);
      
      // フォームをクリア
      setFormData({
        role: '居場所スタッフ',
        title: '',
        body: '',
        rating_place: 3,
        rating_home: 3,
        rating_self: 3,
        rating_print: 3,
        rating_design: 3,
      });

      // レビューを再取得
      const reviews = await fetchSiteReviews(site.id);
      setAllReviews(reviews);

      alert('口コミを投稿しました！');
    } catch (err) {
      console.error('Failed to submit review:', err);
      alert('口コミの投稿に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  // 役に立ったボタン
  const handleHelpful = async (reviewId: string) => {
    try {
      setUpdatingHelpful(reviewId);
      await incrementReviewHelpful(reviewId);
      
      // ローカル状態を更新
      setAllReviews((prev) =>
        prev.map((review) =>
          review.id === reviewId
            ? { ...review, helpful_count: review.helpful_count + 1 }
            : review
        )
      );
    } catch (err) {
      console.error('Failed to increment helpful:', err);
      alert('エラーが発生しました。');
    } finally {
      setUpdatingHelpful(null);
    }
  };

  // 用途ラベルの表示用関数
  const getUsageLabel = (value: 1 | 2 | 3) => {
    switch (value) {
      case 1:
        return '△';
      case 2:
        return '○';
      case 3:
        return '◎';
      default:
        return '○';
    }
  };

  const getUsageBadgeClass = (value: 1 | 2 | 3) => {
    switch (value) {
      case 1:
        return 'bg-gray-100 text-gray-600';
      case 2:
        return 'bg-teal-100 text-teal-700';
      case 3:
        return 'bg-teal-600 text-white';
      default:
        return 'bg-teal-100 text-teal-700';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-center text-gray-600">読み込み中…</p>
        </div>
      </Layout>
    );
  }

  if (error || !site) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error || 'サイトが見つかりませんでした。'}</p>
          </div>
          <Link
            to="/sites"
            className="inline-block mt-4 text-teal-600 hover:text-teal-700 cursor-pointer"
          >
            ← 教材サイト一覧に戻る
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* サイト情報 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{site.name}</h1>
          
          {site.tags && site.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {site.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-teal-50 text-teal-700 text-sm rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {site.description && (
            <p className="text-gray-700 mb-4">{site.description}</p>
          )}

          <a
            href={site.base_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer text-lg font-bold"
          >
            <i className="ri-external-link-line text-2xl"></i>
            <span className="flex items-center gap-2">
              サイトを開く
              <span className="text-2xl font-black text-white" style={{ textShadow: '2px 2px 0 #14B8A6, -2px -2px 0 #14B8A6, 2px -2px 0 #14B8A6, -2px 2px 0 #14B8A6' }}>
                キョウコレ
              </span>
            </span>
          </a>
        </div>

        {/* 評価セクション */}
        {siteRating && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">評価</h2>

            {/* ★評価サマリー */}
            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* わかりやすさ・学びやすさ */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">わかりやすさ・学びやすさ</span>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`text-lg ${
                            star <= siteRating.rating_clarity
                              ? 'ri-star-fill text-amber-400'
                              : 'ri-star-line text-gray-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {siteRating.rating_clarity.toFixed(1)}
                    </span>
                  </div>
                </div>

                {/* 自主学習のしやすさ */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">自主学習のしやすさ</span>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`text-lg ${
                            star <= siteRating.rating_self_study
                              ? 'ri-star-fill text-amber-400'
                              : 'ri-star-line text-gray-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {siteRating.rating_self_study.toFixed(1)}
                    </span>
                  </div>
                </div>

                {/* 印刷・配布のしやすさ */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">印刷・配布のしやすさ</span>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`text-lg ${
                            star <= siteRating.rating_printability
                              ? 'ri-star-fill text-amber-400'
                              : 'ri-star-line text-gray-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {siteRating.rating_printability.toFixed(1)}
                    </span>
                  </div>
                </div>

                {/* デザイン・見やすさ */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">デザイン・見やすさ</span>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`text-lg ${
                            star <= siteRating.rating_design
                              ? 'ri-star-fill text-amber-400'
                              : 'ri-star-line text-gray-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {siteRating.rating_design.toFixed(1)}
                    </span>
                  </div>
                </div>

                {/* 独自性・価値 */}
                <div className="flex items-center justify-between md:col-span-2">
                  <span className="text-sm text-gray-700">独自性・価値</span>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`text-lg ${
                            star <= siteRating.rating_uniqueness
                              ? 'ri-star-fill text-amber-400'
                              : 'ri-star-line text-gray-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {siteRating.rating_uniqueness.toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 用途ラベルのバッジ */}
            <div className="border-t border-gray-200 pt-6 mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">どんな場面に向いているか</h3>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">居場所向け：</span>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getUsageBadgeClass(
                      siteRating.usage_ibasho
                    )}`}
                  >
                    {getUsageLabel(siteRating.usage_ibasho)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">家庭向け：</span>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getUsageBadgeClass(
                      siteRating.usage_home
                    )}`}
                  >
                    {getUsageLabel(siteRating.usage_home)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">自主学習向け：</span>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getUsageBadgeClass(
                      siteRating.usage_self
                    )}`}
                  >
                    {getUsageLabel(siteRating.usage_self)}
                  </span>
                </div>
              </div>
            </div>

            {/* コメント */}
            <div className="border-t border-gray-200 pt-6">
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">良かったところ</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{siteRating.good_points}</p>
              </div>

              {siteRating.caution_points && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">注意ポイント</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{siteRating.caution_points}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 評価セクション（口コミベース） */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">口コミ評価</h2>

          {averages ? (
            <div className="space-y-4">
              <div className="pb-4 border-b border-gray-200">
                <div className="text-sm text-gray-600 mb-2">総合評価</div>
                <StarRating value={averages.overallAvg} showNumber />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SITE_REVIEW_RATING_ITEMS.map((item) => {
                  const avgValue = averages[`${item.key.replace('rating_', '')}Avg` as keyof typeof averages] as number;
                  return (
                    <div key={item.key}>
                      <div className="text-sm text-gray-600 mb-1">{item.label}</div>
                      <StarRating value={avgValue} showNumber />
                    </div>
                  );
                })}
              </div>

              <p className="text-xs text-gray-500 mt-4">
                ※ 直近3か月以内、または「役に立った」が30以上の口コミを集計しています
              </p>
            </div>
          ) : (
            <p className="text-gray-600">まだ口コミがありません。</p>
          )}
        </div>

        {/* 口コミ一覧 */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">口コミ一覧</h2>

          {visibleReviews.length === 0 ? (
            <p className="text-gray-600">まだ口コミがありません。</p>
          ) : (
            <div className="space-y-4">
              {visibleReviews.map((review) => (
                <div
                  key={review.id}
                  className="bg-white border border-gray-200 rounded-lg p-6"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      {review.title && (
                        <h3 className="font-bold text-gray-900 mb-1">{review.title}</h3>
                      )}
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        {review.role && (
                          <div className="flex items-center gap-2">
                            <span>{review.role}</span>
                            {review.role === 'AI紹介メモ（自動生成）' && (
                              <span className="inline-flex items-center px-2 py-0.5 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 text-xs font-semibold rounded-full border border-purple-200">
                                <i className="ri-robot-line mr-1"></i>
                                AI
                              </span>
                            )}
                          </div>
                        )}
                        <span>
                          {new Date(review.created_at).toLocaleDateString('ja-JP')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {review.body && (
                    <p className="text-gray-700 mb-4 whitespace-pre-wrap">{review.body}</p>
                  )}

                  <div className="mt-3 flex flex-wrap gap-3 text-xs mb-4">
                    {SITE_REVIEW_RATING_ITEMS.map((item) => (
                      <div key={item.key} className="flex items-center gap-1">
                        <span className="text-gray-500">{item.label}:</span>
                        <span className="text-amber-500">
                          {'★'.repeat(Math.floor(review[item.key] ?? 0))}
                          {'☆'.repeat(5 - Math.floor(review[item.key] ?? 0))}
                        </span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => handleHelpful(review.id)}
                    disabled={updatingHelpful === review.id}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-thumb-up-line"></i>
                    役に立った（{review.helpful_count}）
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 口コミ投稿フォーム */}
        <div ref={reviewFormRef} className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">口コミを投稿する</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                立場
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option>居場所スタッフ</option>
                <option>保護者</option>
                <option>ボランティア</option>
                <option>その他</option>
                {/* AI紹介メモは通常の投稿フォームでは選択不可（非表示） */}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                タイトル <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                本文 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                rows={5}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
              />
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">評価</h3>
              <div className="grid gap-4 md:grid-cols-2">
                {SITE_REVIEW_RATING_ITEMS.map((item) => (
                  <div key={item.key} className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-900">
                      {item.label}
                    </label>
                    {item.help && (
                      <p className="text-xs text-gray-500 mb-1">{item.help}</p>
                    )}
                    <select
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 border"
                      value={formData[item.key] ?? 3}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          [item.key]: Number(e.target.value),
                        }))
                      }
                    >
                      {[1, 2, 3, 4, 5].map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {submitting ? '投稿中…' : '口コミを投稿する'}
            </button>
          </form>
        </div>

        {/* このサイトを使った活用レシピ */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            このサイトを使った活用レシピ
          </h2>
          {recipesLoading ? (
            <p className="text-gray-600">読み込み中…</p>
          ) : recipes.length === 0 ? (
            <p className="text-gray-600">
              このサイトを使った活用レシピはまだありません
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {recipes.map((recipe) => (
                <div
                  key={recipe.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <h3 className="font-bold text-gray-900 mb-2">
                    {recipe.title}
                  </h3>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {recipe.grade_min !== null && recipe.grade_max !== null && (
                      <span className="text-xs px-2 py-1 bg-teal-50 text-teal-700 rounded-full">
                        {recipe.grade_min === recipe.grade_max
                          ? recipe.grade_min <= 6
                            ? `小${recipe.grade_min}`
                            : recipe.grade_min <= 9
                            ? `中${recipe.grade_min - 6}`
                            : `高${recipe.grade_min - 9}`
                          : `${
                              recipe.grade_min <= 6
                                ? `小${recipe.grade_min}`
                                : recipe.grade_min <= 9
                                ? `中${recipe.grade_min - 6}`
                                : `高${recipe.grade_min - 9}`
                            }〜${
                              recipe.grade_max <= 6
                                ? `小${recipe.grade_max}`
                                : recipe.grade_max <= 9
                                ? `中${recipe.grade_max - 6}`
                                : `高${recipe.grade_max - 9}`
                            }`}
                      </span>
                    )}
                    {recipe.scene && (
                      <span className="text-xs px-2 py-1 bg-teal-50 text-teal-700 rounded-full">
                        {recipe.scene}
                      </span>
                    )}
                    {recipe.time_minutes && (
                      <span className="text-xs px-2 py-1 bg-teal-50 text-teal-700 rounded-full">
                        {recipe.time_minutes}分
                      </span>
                    )}
                  </div>
                  {recipe.summary && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {recipe.summary.length > 80
                        ? `${recipe.summary.slice(0, 80)}...`
                        : recipe.summary}
                    </p>
                  )}
                  {recipe.helpful_count > 0 && (
                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
                      <i className="ri-thumb-up-line"></i>
                      <span>役に立った{recipe.helpful_count}件</span>
                    </div>
                  )}
                  <button
                    onClick={() => navigate(`/recipes/${recipe.id}`)}
                    className="w-full px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    レシピを見る
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ナッジモーダル */}
      <NudgeModal
        isOpen={nudgeModal.isVisible}
        onClose={nudgeModal.close}
        title={`「${site?.name ?? ''}」の口コミを書いてみませんか？`}
        description="1分でOK。ほかの人の参考になります。"
        primaryButtonLabel="口コミを書く"
        secondaryButtonLabel="キャンセル"
        onPrimaryClick={scrollToReviewForm}
      />
    </Layout>
  );
}
